import { ReactNode, createContext, useContext, useRef, useState } from "react";

import {
  AREAS,
  Attempt,
  MIN_DETAILS_LENGTH,
  REPORT_STEP,
  REPORT_TEXT_LIMIT,
  ReportReason,
  ReportingArea,
  SubmitReport,
  areaForStep,
} from "../../lib/reports";

type ReportState = {
  reason?: ReportReason;
  details: string;
  // choosing: the list. writing: the Something else text box. sending and failed keep
  // the reason and draft. sent: the confirmation.
  status: "choosing" | "writing" | "sending" | "failed" | "sent";
};

type Origin = { area: ReportingArea; step: string };

type ReportStoreValue = {
  reports: Record<ReportingArea, ReportState>;
  // The step the report page was opened from. It stays after the page closes, so a
  // return to that step can be recognized.
  origin?: Origin;
  canReport: (area: ReportingArea) => boolean;
  open: (area: ReportingArea, step: string) => void;
  close: () => void;
  // True while the step the report page was opened from is showing again, so it can
  // skip its one-time work.
  returnedFromReport: boolean;
  captureAttempt: (area: ReportingArea, attempt: Attempt) => void;
  // A predefined reason is sent; Something else opens the text box.
  chooseReason: (area: ReportingArea, reason: ReportReason) => void;
  setDetails: (area: ReportingArea, details: string) => void;
  // From the text box or a failed send, Back returns to the choices; otherwise it closes the page.
  back: (area: ReportingArea) => void;
  submit: (area: ReportingArea) => Promise<void>;
};

type ReportProviderProps = {
  sessionId?: string;
  // Current step, used to count visits to each area's steps.
  step: string;
  goTo: (step: string) => void;
  // Undefined while reporting is unavailable.
  submitReport?: SubmitReport;
  children: ReactNode;
};

// `reason` is listed so a reset clears it: update() merges, and a missing key keeps the old value.
const initialState = (): ReportState => ({
  reason: undefined,
  details: "",
  status: "choosing",
});

const initialReports = () =>
  Object.fromEntries(
    Object.keys(AREAS).map((area) => [area, initialState()])
  ) as Record<ReportingArea, ReportState>;

const ReportStore = createContext<ReportStoreValue | null>(null);

export const useReport = () => useContext(ReportStore);

// The area whose report link the step offers right now, if any.
export const useReportArea = (step: string) => {
  const reporting = useReport();
  const area = areaForStep(step);
  return reporting && area && reporting.canReport(area) ? area : undefined;
};

// Counts separate visits to each area's steps and remembers the step before this one.
// Moving between steps of one area is a single visit, and the report page does not end a visit.
const useStepHistory = (step: string) => {
  const [seen, setSeen] = useState<{
    step: string;
    previous: string;
    area?: ReportingArea;
    visits: Partial<Record<ReportingArea, number>>;
  }>({ step: "", previous: "", visits: {} });
  // Setting state while rendering is React's way to reset state when a prop changes.
  if (step !== seen.step) {
    const area = step === REPORT_STEP ? seen.area : areaForStep(step);
    const isNewVisit = area && area !== seen.area;
    setSeen({
      step,
      previous: seen.step,
      area,
      visits: isNewVisit
        ? { ...seen.visits, [area]: (seen.visits[area] ?? 0) + 1 }
        : seen.visits,
    });
  }
  return seen;
};

const ReportSession = ({
  sessionId,
  step,
  goTo,
  submitReport,
  children,
}: ReportProviderProps & { sessionId: string }) => {
  const [reports, setReports] = useState(initialReports);
  const [origin, setOrigin] = useState<Origin>();
  const { visits, previous } = useStepHistory(step);
  const attempts = useRef<Partial<Record<ReportingArea, Attempt>>>({});
  const pending = useRef(new Set<ReportingArea>());
  // One saved ID per area. The signature covers everything sent, so an unchanged
  // retry reuses its ID for deduplication; a changed report gets a new one.
  const intents = useRef<
    Partial<
      Record<
        ReportingArea,
        { signature: string; submissionId: string; createdAt: string }
      >
    >
  >({});
  // A new attempt means a new report, so any saved ID is dropped.
  const captureAttempt = (area: ReportingArea, attempt: Attempt) => {
    attempts.current[area] = { ...attempt };
    delete intents.current[area];
  };

  const canReport = (area: ReportingArea) => {
    return (
      Boolean(submitReport) &&
      // Report IDs come from crypto.randomUUID, which older browsers lack.
      typeof globalThis.crypto?.randomUUID === "function" &&
      (visits[area] ?? 0) >= (AREAS[area].minVisits ?? 1)
    );
  };

  const open = (area: ReportingArea, from: string) => {
    // Reopening resumes a report that is being sent; otherwise it starts fresh.
    if (!pending.current.has(area)) reset(area);
    setOrigin({ area, step: from });
    goTo(REPORT_STEP);
  };

  const close = () => {
    if (origin) goTo(origin.step);
  };

  const update = (area: ReportingArea, changes: Partial<ReportState>) => {
    setReports((current) => ({
      ...current,
      [area]: { ...current[area], ...changes },
    }));
  };

  const reset = (area: ReportingArea) => update(area, initialState());

  const setDetails = (area: ReportingArea, details: string) =>
    update(area, { details: details.slice(0, REPORT_TEXT_LIMIT) });

  const back = (area: ReportingArea) => {
    const { status, reason } = reports[area];
    // A text-box send can't be left; a send from the list finishes in the background.
    if (status === "sending" && reason === "other") return;
    if (status === "writing" || status === "failed") reset(area);
    else close();
  };

  const send = async (
    area: ReportingArea,
    reason: ReportReason,
    from: string
  ) => {
    const report = reports[area];
    if (!submitReport || pending.current.has(area)) return;

    const details = reason === "other" ? report.details : "";
    if (reason === "other" && details.trim().length < MIN_DETAILS_LENGTH)
      return;
    const attempt = attempts.current[area];
    const signature = JSON.stringify([area, reason, details, from, attempt]);
    let intent = intents.current[area];
    if (intent?.signature !== signature) {
      intent = {
        signature,
        submissionId: crypto.randomUUID(),
        createdAt: new Date().toISOString(),
      };
      intents.current[area] = intent;
    }

    pending.current.add(area);
    update(area, { reason, status: "sending" });
    try {
      await submitReport(sessionId, {
        submissionId: intent.submissionId,
        createdAt: intent.createdAt,
        area,
        reason,
        details,
        step: from,
        attempt,
      });
      delete intents.current[area];
      update(area, { ...initialState(), status: "sent" });
    } catch (error) {
      console.error("Failed to send user report", error);
      // Keep the draft so the user can try again.
      update(area, { status: "failed" });
    } finally {
      pending.current.delete(area);
    }
  };

  // A send keeps the step it started from, so it survives the page closing.
  const submit = async (area: ReportingArea) => {
    // Nothing to send before the report page has been opened.
    const { reason } = reports[area];
    if (reason && origin) await send(area, reason, origin.step);
  };

  const chooseReason = (area: ReportingArea, reason: ReportReason) => {
    if (reason === "other") {
      update(area, { reason, status: "writing" });
    } else if (origin) {
      void send(area, reason, origin.step);
    }
  };

  return (
    <ReportStore.Provider
      value={{
        reports,
        origin,
        canReport,
        open,
        close,
        returnedFromReport: previous === REPORT_STEP && step === origin?.step,
        captureAttempt,
        chooseReason,
        setDetails,
        back,
        submit,
      }}
    >
      {children}
    </ReportStore.Provider>
  );
};

export const ReportProvider = ({ sessionId, ...props }: ReportProviderProps) =>
  sessionId ? (
    <ReportSession key={sessionId} sessionId={sessionId} {...props} />
  ) : (
    <>{props.children}</>
  );
